import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { differenceInDays, format, parseISO } from 'date-fns'

export default function Dashboard({ session }) {
  const [profile, setProfile] = useState(null)
  const [season, setSeason] = useState(null)
  const [matches, setMatches] = useState([])
  const [goals, setGoals] = useState([])
  const [networking, setNetworking] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const uid = session.user.id
    Promise.all([
      supabase.from('profiles').select('*').eq('id', uid).single(),
      supabase.from('seasons').select('*').eq('user_id', uid).eq('is_current', true).maybeSingle(),
      supabase.from('matches').select('*').eq('user_id', uid).order('match_date', { ascending: false }).limit(5),
      supabase.from('career_goals').select('*').eq('user_id', uid).eq('status', 'active'),
      supabase.from('networking').select('*').eq('user_id', uid).order('date_contacted', { ascending: false }).limit(5),
    ]).then(([p, s, m, g, n]) => {
      setProfile(p.data)
      setSeason(s.data)
      setMatches(m.data || [])
      setGoals(g.data || [])
      setNetworking(n.data || [])
      setLoading(false)
    })
  }, [session])

  if (loading) return <div style={{ color: 'var(--muted)', padding: 40 }}>Loading...</div>

  const totalGoals = matches.reduce((a, m) => a + (m.goals || 0), 0)
  const totalAssists = matches.reduce((a, m) => a + (m.assists || 0), 0)
  const totalApps = matches.length
  const totalMinutes = matches.reduce((a, m) => a + (m.minutes_played || 0), 0)
  const avgRating = matches.filter(m => m.rating).length > 0
    ? (matches.filter(m => m.rating).reduce((a, m) => a + m.rating, 0) / matches.filter(m => m.rating).length).toFixed(1)
    : '-'

  const daysToContractEnd = season?.contract_end
    ? differenceInDays(parseISO(season.contract_end), new Date())
    : null

  const contractUrgency = daysToContractEnd !== null
    ? daysToContractEnd < 60 ? 'danger' : daysToContractEnd < 180 ? 'warning' : 'good'
    : null

  if (!season) {
    const firstName = profile?.full_name ? profile.full_name.split(' ')[0] : null
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '48px 0' }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={{ fontFamily: 'Bebas Neue', fontSize: 56, letterSpacing: 6, color: 'var(--gold)', lineHeight: 1 }}>JNM</div>
          <div style={{ fontSize: 11, color: 'var(--muted)', letterSpacing: 4, textTransform: 'uppercase', marginTop: 6 }}>Career Hub</div>
        </div>

        {/* Welcome heading */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <div style={{ fontFamily: 'Bebas Neue', fontSize: 40, letterSpacing: 2, marginBottom: 14 }}>
            {firstName ? `Welcome, ${firstName}` : 'Welcome'}
          </div>
          <div style={{ fontSize: 15, color: 'var(--muted)', fontWeight: 300, lineHeight: 1.8, maxWidth: 460, margin: '0 auto' }}>
            Your personal football career dashboard. Track your season stats, manage club outreach, set career goals, and keep your full career history — all in one place.
          </div>
        </div>

        {/* Step 1 CTA */}
        <div style={{ background: 'var(--dark2)', border: '1px solid var(--gold-border)', borderRadius: 8, padding: 32, marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
            <div style={{ background: 'var(--gold)', color: '#080806', width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, flexShrink: 0, marginTop: 2 }}>1</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 6 }}>Set up your current season</div>
              <div style={{ fontSize: 14, color: 'var(--muted)', fontWeight: 300, lineHeight: 1.7, marginBottom: 20 }}>
                Add your current club, league, and contract details. Once your season is set up, you can start logging matches and tracking your performance.
              </div>
              <Link to="/season" className="btn btn-primary">Set Up Season</Link>
            </div>
          </div>
        </div>

        {/* Preview of other features */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, opacity: 0.45 }}>
          {[
            { icon: '📋', label: 'Career History', desc: 'Every club you\'ve played for' },
            { icon: '◎', label: 'Career Goals', desc: 'Plan your next move' },
            { icon: '⟳', label: 'Networking', desc: 'Track your club outreach' },
          ].map(item => (
            <div key={item.label} style={{ background: 'var(--dark2)', border: '1px solid var(--border)', borderRadius: 6, padding: '20px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>{item.icon}</div>
              <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.5, marginBottom: 4 }}>{item.label}</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.5 }}>{item.desc}</div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="flex-between mb-32">
        <div>
          <div className="page-title">Dashboard</div>
          <div className="page-sub">
            {profile?.full_name ? `Welcome back, ${profile.full_name.split(' ')[0]}` : 'Welcome back'}
            {season ? ` — ${season.club_name}, ${season.country}` : ''}
          </div>
        </div>
      </div>

      {/* Contract expiry alert */}
      {daysToContractEnd !== null && daysToContractEnd < 180 && (
        <div style={{
          background: contractUrgency === 'danger' ? 'rgba(229,57,53,0.1)' : 'rgba(253,215,0,0.08)',
          border: `1px solid ${contractUrgency === 'danger' ? 'rgba(229,57,53,0.3)' : 'var(--gold-border)'}`,
          borderRadius: 6,
          padding: '14px 20px',
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
        }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, color: contractUrgency === 'danger' ? '#E53935' : 'var(--gold)', marginBottom: 2 }}>
              {contractUrgency === 'danger' ? '⚠ Contract expiring soon' : '◎ Contract update'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>
              Your contract at {season?.club_name} expires in {daysToContractEnd} days ({format(parseISO(season.contract_end), 'dd MMM yyyy')})
            </div>
          </div>
          <Link to="/goals" className="btn btn-ghost" style={{ whiteSpace: 'nowrap', fontSize: 12 }}>Plan Next Move</Link>
        </div>
      )}

      {/* Season stats */}
      <div className="mb-8">
        <span className="tag">This Season</span>
      </div>
      <div className="grid-4 mb-32">
        <div className="stat-card">
          <div className="stat-value">{totalApps}</div>
          <div className="stat-label">Appearances</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{totalGoals}</div>
          <div className="stat-label">Goals</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{totalAssists}</div>
          <div className="stat-label">Assists</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{avgRating}</div>
          <div className="stat-label">Avg Rating</div>
        </div>
      </div>

      <div className="grid-2 mb-32">
        {/* Recent matches */}
        <div className="card">
          <div className="flex-between mb-16">
            <div className="card-title">Recent Matches</div>
            <Link to="/season" style={{ fontSize: 12, color: 'var(--gold)', textDecoration: 'none' }}>View all</Link>
          </div>
          {matches.length === 0 ? (
            <div className="empty-state" style={{ padding: '24px 0' }}>
              <div style={{ color: 'var(--muted)', fontSize: 13 }}>No matches logged yet</div>
              <Link to="/season" className="btn btn-ghost mt-8" style={{ fontSize: 12 }}>Log a Match</Link>
            </div>
          ) : (
            <div>
              {matches.slice(0, 5).map(m => (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>vs {m.opponent || 'Unknown'}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                      {m.match_date ? format(parseISO(m.match_date), 'dd MMM') : ''} · {m.minutes_played}' · {m.goals}G {m.assists}A
                    </div>
                  </div>
                  <div>
                    {m.rating ? (
                      <span style={{ fontFamily: 'Bebas Neue', fontSize: 20, color: 'var(--gold)', letterSpacing: 1 }}>{m.rating}<span style={{ fontSize: 12, color: 'var(--muted)' }}>/5</span></span>
                    ) : (
                      <span style={{ fontSize: 12, color: 'var(--muted)' }}>-</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Active goals */}
        <div className="card">
          <div className="flex-between mb-16">
            <div className="card-title">Active Goals</div>
            <Link to="/goals" style={{ fontSize: 12, color: 'var(--gold)', textDecoration: 'none' }}>View all</Link>
          </div>
          {goals.length === 0 ? (
            <div className="empty-state" style={{ padding: '24px 0' }}>
              <div style={{ color: 'var(--muted)', fontSize: 13 }}>No goals set yet</div>
              <Link to="/goals" className="btn btn-ghost mt-8" style={{ fontSize: 12 }}>Set a Goal</Link>
            </div>
          ) : (
            <div>
              {goals.slice(0, 4).map(g => (
                <div key={g.id} style={{ padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>{g.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', display: 'flex', gap: 12 }}>
                    {g.target_club && <span>🎯 {g.target_club}</span>}
                    {g.target_date && <span>📅 {format(parseISO(g.target_date), 'MMM yyyy')}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent networking */}
      <div className="card">
        <div className="flex-between mb-16">
          <div className="card-title">Recent Outreach</div>
          <Link to="/networking" style={{ fontSize: 12, color: 'var(--gold)', textDecoration: 'none' }}>View all</Link>
        </div>
        {networking.length === 0 ? (
          <div className="empty-state" style={{ padding: '24px 0' }}>
            <div style={{ color: 'var(--muted)', fontSize: 13 }}>No outreach logged yet</div>
            <Link to="/networking" className="btn btn-ghost mt-8" style={{ fontSize: 12 }}>Log Outreach</Link>
          </div>
        ) : (
          <div>
            {networking.map(n => (
              <div key={n.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{n.club_name}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                    {n.contact_name && <span>{n.contact_name} · </span>}
                    {n.date_contacted ? format(parseISO(n.date_contacted), 'dd MMM yyyy') : ''}
                  </div>
                </div>
                <span className={`badge ${n.status === 'positive' ? 'badge-green' : n.status === 'negative' ? 'badge-red' : n.status === 'trial' ? 'badge-gold' : 'badge-grey'}`}>
                  {n.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
